import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import useConnectivity from "./useConnectivity";
import useLocalVideoStorage from "./useLocalVideoStorage";
import { toast } from "react-toastify";
import TelemetryService from "../shared/services/TelemetryService";
import { EventType } from "../shared/services/TelemetryService.types";
import VideoUploadService from "../services/VideoUploadService";

// Removed FFmpeg configuration types

// Tipos para estado do vídeo
export type VideoStatus =
  | "idle"
  | "recording"
  | "processing"
  | "uploading"
  | "ready"
  | "error";

// Interface para o objeto camera
interface EnhancedCamera {
  mediaRecorder: MediaRecorder | null;
  chunksRef: React.MutableRefObject<Blob[]>;
  videoRef: React.MutableRefObject<HTMLVideoElement>;
  canvasRef: React.MutableRefObject<HTMLCanvasElement>;
  previewRef: React.MutableRefObject<HTMLVideoElement>;
  videoUrl: string;
  videoSize: string;
  videoMimeType: string;
  originalBlob: Blob | null;
  isCaptured: boolean;
  hasPermissions: boolean;
  setVideoUrl: (url: string) => void;
  setVideoSize: (size: string) => void;
  setVideoMimeType: (type: string) => void;
  setOriginalBlob: (blob: Blob | null) => void;
  setIsCaptured: (isCaptured: boolean) => void;
  setHasPermissions: (hasPermissions: boolean) => void;
  handleCaptureBase: () => void;
  blobToBase64: (blob: Blob) => Promise<string>;
  releaseCamera: () => void;
  requestMediaPermissions: () => Promise<MediaStream>;
  getVideoConstraints: () => MediaStreamConstraints;
  getRecordingOptions: () => MediaRecorderOptions;
  isIOS: () => boolean;
  isAndroid: () => boolean;
  setMediaRecorder: (recorder: MediaRecorder | null) => void;
}

/**
 * Hook aprimorado para gravação de vídeo com recursos de resiliência
 */
const useEnhancedCamera = () => {
  // Estados
  const [videoStatus, setVideoStatus] = useState<VideoStatus>("idle");
  const [error, setError] = useState<{ type: string; message: string } | null>(
    null
  );

  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isCaptured, setIsCaptured] = useState<boolean>(false);
  const [hasPermissions, setHasPermissions] = useState<boolean>(false);
  const [hasCamera, setHasCamera] = useState(false);
  const [hasMicrophone, setHasMicrophone] = useState(false);
  const startTimeRef = useRef<number | null>(null);

  // Refs para valores que não devem causar re-renders
  const correlationIdRef = useRef<string>("");
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Criar refs fora do useMemo
  const chunksRef = useRef<Blob[]>([]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewRef = useRef<HTMLVideoElement>(null);

  // Estados para o vídeo
  const [videoUrl, setVideoUrlState] = useState<string>("");
  const [videoSize, setVideoSizeState] = useState<string>("");
  const [videoMimeType, setVideoMimeTypeState] = useState<string>("");
  const [originalBlob, setOriginalBlobState] = useState<Blob | null>(null);

  // Hooks externos
  const { isOnline, connectionQuality, latency } = useConnectivity();
  const { saveVideoLocally } = useLocalVideoStorage();

  // Funções wrapper para os setters de estado
  const setVideoUrl = useCallback((url: string) => {
    setVideoUrlState(url);
  }, []);

  const setVideoSize = useCallback((size: string) => {
    setVideoSizeState(size);
  }, []);

  const setVideoMimeType = useCallback((type: string) => {
    setVideoMimeTypeState(type);
  }, []);

  const setOriginalBlob = useCallback((blob: Blob | null) => {
    setOriginalBlobState(blob);
  }, []);

  // Funções utilitárias
  const isIOS = useCallback(() => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }, []);

  const isAndroid = useCallback(() => {
    return /Android/.test(navigator.userAgent);
  }, []);

  const handleCaptureBase = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext("2d");
      const aspectRatio = 4 / 3;
      const videoWidth = videoRef.current.videoWidth;
      const videoHeight = videoRef.current.videoHeight;

      let width = videoWidth;
      let height = width / aspectRatio;

      if (height > videoHeight) {
        height = videoHeight;
        width = height * aspectRatio;
      }

      canvasRef.current.width = width;
      canvasRef.current.height = height;

      context?.drawImage(videoRef.current, 0, 0, width, height);

      setIsCaptured(true);

      if (videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        const tracks = stream.getTracks();
        tracks.forEach((track) => track.stop());
      }
    }
  }, []);

  // Função para converter Blob para base64
  const blobToBase64 = useCallback(async (blob: Blob): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }, []);

  // MediaRecorder state
  const [mediaRecorder, setMediaRecorderState] = useState<MediaRecorder | null>(
    null
  );

  // Memoize camera config
  const camera = useMemo<EnhancedCamera>(
    () => ({
      mediaRecorder,
      chunksRef,
      videoRef,
      canvasRef,
      previewRef,
      videoUrl,
      videoSize,
      videoMimeType,
      originalBlob,
      isCaptured,
      hasPermissions,
      setVideoUrl,
      setVideoSize,
      setVideoMimeType,
      setOriginalBlob,
      setIsCaptured,
      setHasPermissions,
      handleCaptureBase,
      blobToBase64,
      releaseCamera: () => {
        if (mediaRecorder?.stream) {
          mediaRecorder.stream.getTracks().forEach((track) => track.stop());
        }
      },
      requestMediaPermissions: async () => {
        return await navigator.mediaDevices.getUserMedia(getVideoConstraints());
      },
      getVideoConstraints: () => ({
        video: {
          width: { ideal: 480, max: 640 },
          height: { ideal: 360, max: 480 },
          frameRate: { ideal: 20, max: 24 },
          facingMode: "user",
        } as MediaTrackConstraints,
        audio: {
          sampleRate: 44100,
          channelCount: 1, // Mono para reduzir tamanho
          echoCancellation: true,
          noiseSuppression: true,
        },
      }),
      getRecordingOptions: () => ({
        mimeType: "video/webm;codecs=vp8,opus",
        videoBitsPerSecond: isIOS() ? 250_000 : 400_000,
        audioBitsPerSecond: 48_000,
      }),
      isIOS,
      isAndroid,
      setMediaRecorder: (recorder: MediaRecorder | null) => {
        setMediaRecorderState(recorder);
      },
    }),
    [
      mediaRecorder,
      videoUrl,
      videoSize,
      videoMimeType,
      originalBlob,
      isCaptured,
      hasPermissions,
      setVideoUrl,
      setVideoSize,
      setVideoMimeType,
      setOriginalBlob,
      setIsCaptured,
      setHasPermissions,
      handleCaptureBase,
      blobToBase64,
      isIOS,
      isAndroid,
    ]
  );

  // Get video constraints function
  const getVideoConstraints = useCallback(
    () => ({
      video: {
        width: { ideal: 640, max: 640 },
        height: { ideal: 480, max: 480 },
        frameRate: { ideal: 24, max: 24 },
        facingMode: "user",
      } as MediaTrackConstraints,
      audio: true,
    }),
    []
  );

  // Memoize error handler
  const handleMediaError = useCallback((err: unknown) => {
    setVideoStatus("error");
    const errorMessage =
      err instanceof Error ? err.message : "Erro desconhecido";

    setError({
      type: "media_error",
      message: errorMessage,
    });

    TelemetryService.error(
      "useEnhancedCamera",
      "media_error" as EventType,
      { error: errorMessage },
      correlationIdRef.current
    );
  }, []);

  // Memoize timer clear function
  const clearRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  /**
   * Trata erros específicos durante a gravação
   */
  const handleRecordingError = useCallback(
    (event: any) => {
      clearRecordingTimer();

      const errorType = "unexpected_error";
      const errorMessage =
        "Ocorreu um erro durante a gravação. Por favor, tente novamente.";

      setError({ type: errorType, message: errorMessage });
      setVideoStatus("error");

      TelemetryService.error(
        "useEnhancedCamera",
        "video_record_error" as EventType,
        {
          event,
          errorMessage,
        },
        correlationIdRef.current
      );

      toast.error(errorMessage);
    },
    [clearRecordingTimer]
  );

  // Simplified recording result processor - no compression
  const processRecordingResult = useCallback(async () => {
    try {
      setVideoStatus("processing");

      const mimeType = camera.mediaRecorder?.mimeType || "video/webm";
      camera.setVideoMimeType(mimeType);

      const blob = new Blob(camera.chunksRef.current, { type: mimeType });
      const sizeInMB = blob.size / (1024 * 1024);

      // Use original blob without compression
      camera.setOriginalBlob(blob);
      camera.setVideoSize(sizeInMB.toFixed(2));

      const isSafariOrIOS = () => {
        const ua = navigator.userAgent;
        return (
          /iP(ad|od|hone)/i.test(ua) || // iOS
          (/Safari/i.test(ua) && !/Chrome|CriOS|CrMo/i.test(ua)) // Safari
        );
      };

      if (isSafariOrIOS()) {
        // SAFARI OU IPHONE - usar FileReader para gerar base64
        const reader = new FileReader();
        reader.onloadend = () => {
          camera.setVideoUrl(reader.result as string);
          if (camera.videoRef.current) {
            camera.videoRef.current.load();
          }
        };
        reader.readAsDataURL(blob);
      } else {
        // OUTROS (Chrome, Firefox etc) - usar URL.createObjectURL
        const objectUrl = URL.createObjectURL(blob);
        camera.setVideoUrl(objectUrl);
        if (camera.videoRef.current) {
          camera.videoRef.current.load();
        }
      }

      setVideoStatus("ready");

      TelemetryService.info(
        "useEnhancedCamera",
        "video_record_stop" as EventType,
        {
          sizeInMB: sizeInMB.toFixed(2),
          durationInSeconds: recordingDuration,
          mimeType,
        },
        correlationIdRef.current
      );

      if (!isOnline) {
        const savedId = await saveVideoLocally(blob, {
          mimeType,
          fileType: 5,
        });

        if (savedId) {
          toast.info(
            "Vídeo salvo localmente e será enviado quando a conexão for restaurada."
          );
          TelemetryService.info(
            "useEnhancedCamera",
            "video_saved_locally" as EventType,
            {
              savedId,
              sizeInMB: sizeInMB.toFixed(2),
              mimeType,
            },
            correlationIdRef.current
          );
        }
      }
    } catch (error) {
      setVideoStatus("error");
      setError({
        type: "unexpected_error",
        message: "Erro ao processar o vídeo gravado",
      });

      TelemetryService.error(
        "useEnhancedCamera",
        "video_record_stop_error" as EventType,
        {
          error: error instanceof Error ? error.message : "Erro desconhecido",
          context: "video_processing_error",
        },
        correlationIdRef.current
      );
      toast.error("Ocorreu um erro inesperado ao processar seu vídeo.");
    }
  }, [camera, recordingDuration, isOnline, saveVideoLocally]);

  /**
   * Inicializar correlationId no mount
   */
  useEffect(() => {
    const id = TelemetryService.createCorrelationId(`video_${Date.now()}`);
    correlationIdRef.current = id;

    // Verificar permissões iniciais
    const checkInitialPermissions = async () => {
      try {
        await camera.requestMediaPermissions();
      } catch (error) {
        TelemetryService.error(
          "useEnhancedCamera",
          "media_permission_change" as EventType,
          {
            error: error instanceof Error ? error.message : "Erro desconhecido",
          },
          id // Usar o id local em vez de correlationId do estado
        );
      }
    };

    checkInitialPermissions();
  }, [camera]); // Remover correlationId da dependência

  /**
   * Inicia a gravação com tratamento de erros aprimorado
   */

  interface ConnectionStatus {
    connectionType: string | null;
    effectiveConnectionType: string | null;
    isGoodConnection: boolean;
  }

  function getConnectionStatus(): ConnectionStatus {
    const connection =
      (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection;

    if (!connection) {
      // API não suportada
      return {
        connectionType: null,
        effectiveConnectionType: null,
        isGoodConnection: true, // Assumimos que é boa se não sabemos
      };
    }

    const connectionType = connection.type || null;
    const effectiveConnectionType = connection.effectiveType || null;

    // Aqui você pode customizar o que considera "boa conexão"
    const goodConnections = ["wifi", "ethernet", "4g"];

    const isGoodConnection =
      goodConnections.includes(connectionType) ||
      goodConnections.includes(effectiveConnectionType);

    return {
      connectionType,
      effectiveConnectionType,
      isGoodConnection,
    };
  }

  const handleStart = useCallback(async () => {
    const { effectiveConnectionType, isGoodConnection } = getConnectionStatus();

    console.log("Qualidade efetiva:", effectiveConnectionType);
    console.log("Conexão é boa?", isGoodConnection);

    if (!isGoodConnection) {
      toast.error(
        "Sua conexão pode não ideal para essa operação, recomendamos o uso de uma conexão wifi."
      );
    }
    try {
      // Reset de estados
      setError(null);
      setRecordingDuration(0);
      chunksRef.current = [];

      // Verificar e solicitar permissões antes de iniciar
      try {
        const stream = await navigator.mediaDevices.getUserMedia(
          getVideoConstraints()
        );

        const videoTrack = stream.getVideoTracks()[0];
        const settings = videoTrack.getSettings();
        const frameRate = settings.frameRate;
        console.log("Frame rate capturado da câmera:", frameRate);

        setHasPermissions(true);

        // Log do evento
        TelemetryService.info(
          "useEnhancedCamera",
          "video_record_start" as EventType,
          { connectionQuality, latency },
          correlationIdRef.current
        );

        // Feedback tátil em dispositivos móveis
        // try {
        //   if (navigator.vibrate) {
        //     navigator.vibrate(200);
        //   }
        // } catch (e) {
        //   // Ignorar se não suportado
        // }

        // Simplified recording options - no hostname-specific configs
        const options = {
          mimeType: "video/webm;codecs=vp8,opus",
          videoBitsPerSecond: isIOS() ? 250_000 : 400_000,
          audioBitsPerSecond: 48_000,
        };

        let recorder;
        try {
          recorder = new MediaRecorder(stream, options);
        } catch (e) {
          TelemetryService.warn(
            "useEnhancedCamera",
            "video_record_start" as EventType,
            {
              error: "Formato específico não suportado, usando formato padrão",
              isIOS: isIOS(),
              isAndroid: isAndroid(),
            },
            correlationIdRef.current
          );

          // Tentar sem opções específicas
          try {
            recorder = new MediaRecorder(stream);
          } catch (fallbackError) {
            TelemetryService.error(
              "useEnhancedCamera",
              "video_record_start_error" as EventType,
              {
                error:
                  fallbackError instanceof Error
                    ? fallbackError.message
                    : "Erro ao criar MediaRecorder",
              },
              correlationIdRef.current
            );

            handleMediaError({
              name: "NotSupportedError",
              message: "Seu dispositivo não suporta gravação no navegador",
            });

            if (stream) {
              stream.getTracks().forEach((track) => track.stop());
            }
            return;
          }
        }

        setMediaRecorderState(recorder);

        // Configurar preview
        if (previewRef.current) {
          previewRef.current.srcObject = stream;
          previewRef.current.play().catch((e) => {
            TelemetryService.error(
              "useEnhancedCamera",
              "video_record_start" as EventType,
              { error: e.message },
              correlationIdRef.current
            );

            handleMediaError(e);
            return;
          });
        }

        // Iniciar gravação
        recorder.start(1000); // Coletar chunks a cada segundo para mais resiliência

        // Coletar chunks de dados
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunksRef.current.push(e.data);
          }
        };

        // Monitorar o status da gravação
        recorder.onstart = () => {
          setVideoStatus("recording");
          startTimeRef.current = Date.now();

          // Iniciar cronômetro
          recordingTimerRef.current = setInterval(() => {
            setRecordingDuration((prev) => prev + 1);
          }, 1000);
        };

        // Tratar erros durante a gravação
        recorder.onerror = (event) => {
          handleRecordingError(event);
        };

        // Monitorar o fim da gravação (não é chamado por handleStop)
        recorder.onstop = () => {
          clearRecordingTimer();
          // processRecordingResult();
        };
      } catch (err) {
        handleMediaError({
          name: "NotAllowedError",
          message: "Permissões de câmera/microfone negadas",
        });
        return;
      }
    } catch (err) {
      handleMediaError(err);
    }
  }, [
    connectionQuality,
    latency,
    getVideoConstraints,
    handleMediaError,
    clearRecordingTimer,
    processRecordingResult,
    isIOS,
    isAndroid,
    handleRecordingError,
  ]);

  /**
   * Para a gravação de vídeo
   */
  const handleStop = useCallback(async (): Promise<boolean> => {
    if (!mediaRecorder || mediaRecorder.state === "inactive") return false;

    try {
      // Parar a gravação
      mediaRecorder.stop();

      // Parar o cronômetro
      clearRecordingTimer();

      const now = Date.now();
      const durationInSeconds = startTimeRef.current
        ? Math.floor((now - startTimeRef.current) / 1000)
        : 0;

      const isValid = durationInSeconds > 6 && durationInSeconds < 60;
      if (!isValid) {
        toast.error("O vídeo deve ter entre 6 e 60 segundos.");
        setVideoStatus("idle");
        setRecordingDuration(0);
        chunksRef.current = [];
        return false;
      }

      // Liberar a camera/microfone
      if (mediaRecorder.stream) {
        mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      }

      await processRecordingResult();

      setVideoStatus("processing");

      const tracks = mediaRecorder.stream.getTracks();
      if (tracks.length > 0) {
        const stream = mediaRecorder.stream;
        const recorderChunks: Blob[] = [];

        stream.getTracks().forEach((track) => {
          track.stop();
        });

        if (chunksRef.current && chunksRef.current.length > 0) {
          const fullBlob = new Blob(chunksRef.current, { type: "video/webm" });

          const sizeInBytes = fullBlob.size;
          const sizeInMB = sizeInBytes / (1024 * 1024);

          console.log(`Tamanho do vídeo: ${sizeInMB.toFixed(2)} MB`);

          // if (sizeInMB > 50) {
          //   // máximo 50MB
          //   console.warn("Vídeo muito grande!");
          //   setVideoStatus("error");
          //   setError({
          //     type: "file_too_large",
          //     message: "O vídeo excede o tamanho permitido.",
          //   });
          //   return;
          // }
        }
      }
      // Processar resultado
      // processRecordingResult();
      return true;
    } catch (error) {
      setVideoStatus("error");
      setError({
        type: "unexpected_error",
        message: "Erro ao finalizar a gravação",
      });

      TelemetryService.error(
        "useEnhancedCamera",
        "video_record_stop" as EventType,
        { error: error instanceof Error ? error.message : "Erro desconhecido" },
        correlationIdRef.current
      );

      return false;
    }
  }, [mediaRecorder, clearRecordingTimer, processRecordingResult]);

  /**
   * Captura uma imagem do vídeo com telemetria
   */
  const handleCapture = useCallback(() => {
    try {
      handleCaptureBase();

      TelemetryService.info(
        "useEnhancedCamera",
        "video_capture" as EventType,
        {
          width: canvasRef.current?.width,
          height: canvasRef.current?.height,
        },
        correlationIdRef.current
      );
    } catch (error) {
      TelemetryService.error(
        "useEnhancedCamera",
        "video_capture" as EventType,
        { error: error instanceof Error ? error.message : "Erro desconhecido" },
        correlationIdRef.current
      );
    }
  }, [handleCaptureBase]);

  /**
   * Converte a URL do vídeo para base64
   */
  const createVideoBase64 = useCallback(
    async (
      finalBlob: Blob
    ): Promise<{
      base64Content: string;
      fileExtension: string;
      mimeType: string;
      originalSize: number;
    }> => {
      try {
        TelemetryService.info(
          "useEnhancedCamera",
          "video_upload_start" as EventType,
          { connectionQuality },
          correlationIdRef.current
        );

        const mimeType = finalBlob.type || "video/webm";
        const originalSize = finalBlob.size;

        // Definir extensão baseado no tipo MIME
        let fileExtension = "webm"; // Default

        if (mimeType.includes("webm")) {
          fileExtension = "webm";
        } else if (mimeType.includes("x-matroska")) {
          fileExtension = "mkv";
        } else if (mimeType.includes("quicktime")) {
          fileExtension = "mov";
        }

        // Manter o MIME type original
        const effectiveMimeType = mimeType;

        // Converter para base64 usando o método definido
        const base64Content = await blobToBase64(finalBlob);

        return {
          base64Content,
          fileExtension,
          mimeType: effectiveMimeType,
          originalSize,
        };
      } catch (error) {
        TelemetryService.error(
          "useEnhancedCamera",
          "video_upload_start" as EventType,
          {
            error: error instanceof Error ? error.message : "Erro desconhecido",
          },
          correlationIdRef.current
        );

        toast.error("Erro ao preparar o vídeo para envio.");
        throw error;
      }
    },
    [connectionQuality, blobToBase64]
  );

  /**
   * Solicita permissões e retorna erro, se houver
   */
  const requestPermissions = useCallback(async (): Promise<string | null> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        getVideoConstraints()
      );

      if (stream) {
        // Liberar recursos
        stream.getTracks().forEach((track) => track.stop());
        setHasPermissions(true);
        return null;
      }

      return "Permissões de câmera/microfone negadas. Por favor, conceda as permissões nas configurações do navegador.";
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Erro ao solicitar permissões";

      TelemetryService.error(
        "useEnhancedCamera",
        "media_permission_change" as EventType,
        { error: message },
        correlationIdRef.current
      );

      return message;
    }
  }, [getVideoConstraints]);

  /**
   * Reinicia o estado da câmera, permitindo nova gravação
   */
  const resetCamera = useCallback(() => {
    console.log("resetCamera called - clearing video state");
    
    // Primeiro, parar qualquer gravação em andamento
    if (mediaRecorder && mediaRecorder.state === "recording") {
      try {
        mediaRecorder.stop();
      } catch (error) {
        console.warn("Error stopping mediaRecorder:", error);
      }
    }

    // Limpar chunks
    chunksRef.current = [];
    
    // Limpar estados de vídeo
    setVideoUrlState("");
    setVideoStatus("idle");
    setError(null);
    setRecordingDuration(0);
    setOriginalBlob(null);
    setVideoSizeState("");
    setVideoMimeTypeState("");

    // Parar cronômetro se estiver rodando
    clearRecordingTimer();

    // Criar um novo ID de correlação para telemetria
    const id = TelemetryService.createCorrelationId(`video_${Date.now()}`);
    correlationIdRef.current = id;

    // Liberar memória de blob anterior de forma segura
    if (videoUrl && videoUrl.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(videoUrl);
      } catch (error) {
        console.warn("Error revoking object URL:", error);
      }
    }

    // Liberar recursos de stream se existirem
    if (mediaRecorder?.stream) {
      mediaRecorder.stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (error) {
          console.warn("Error stopping track:", error);
        }
      });
    }

    if (previewRef.current?.srcObject) {
      const stream = previewRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (error) {
          console.warn("Error stopping preview track:", error);
        }
      });
      previewRef.current.srcObject = null;
    }
  }, [videoUrl, mediaRecorder, clearRecordingTimer]);

  /**
   * Limpa recursos quando o componente é desmontado
   */
  useEffect(() => {
    return () => {
      console.log("useEnhancedCamera cleanup");
      
      clearRecordingTimer();

      // Liberar memória de forma mais segura
      if (videoUrl && videoUrl.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(videoUrl);
        } catch (error) {
          console.warn("Error revoking object URL on cleanup:", error);
        }
      }

      // Liberar câmera/microfone de forma mais robusta
      if (mediaRecorder?.stream) {
        mediaRecorder.stream.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch (error) {
            console.warn("Error stopping track on cleanup:", error);
          }
        });
      }

      if (previewRef.current?.srcObject) {
        const stream = previewRef.current.srcObject as MediaStream;
        stream.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch (error) {
            console.warn("Error stopping preview track on cleanup:", error);
          }
        });
        previewRef.current.srcObject = null;
      }
    };
  }, [videoUrl, mediaRecorder, clearRecordingTimer]);

  const checkDevicePermissions = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const videoDevices = devices.filter(
        (device) => device.kind === "videoinput"
      );
      const audioDevices = devices.filter(
        (device) => device.kind === "audioinput"
      );

      setHasCamera(videoDevices.length > 0);
      setHasMicrophone(audioDevices.length > 0);

      return videoDevices.length > 0 && audioDevices.length > 0;
    } catch (error) {
      console.error("Error checking device permissions:", error);
      return false;
    }
  }, []);

  const initializeDevices = useCallback(async () => {
    try {
      await checkDevicePermissions();
      if (hasCamera && hasMicrophone) {
        const stream = await requestPermissions();
        if (stream === null) {
          setHasPermissions(true);
        }
      }
    } catch (error) {
      console.error("Error initializing devices:", error);
    }
  }, [checkDevicePermissions, hasCamera, hasMicrophone, requestPermissions]);

  /**
   * Faz upload do vídeo para o servidor com suporte a chunks
   */
  const uploadVideo = useCallback(
    async (
      uploadMetadata: any = {}
    ): Promise<{ success: boolean; data?: any; error?: any }> => {
      if (!originalBlob) {
        const errorMessage = "Nenhum vídeo disponível para upload";
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }

      try {
        setVideoStatus("uploading");

        const handleProgress = (progress: any) => {
          // Você pode usar este callback para atualizar a UI com o progresso
          TelemetryService.info(
            "useEnhancedCamera",
            "video_upload_progress" as EventType,
            { progress },
            correlationIdRef.current
          );
        };

        // Usar o serviço de upload em chunks para maior resiliência
        const result = await VideoUploadService.uploadVideo(
          originalBlob,
          {
            mimeType: videoMimeType,
            durationInSeconds: recordingDuration,
            ...uploadMetadata,
          },
          handleProgress,
          correlationIdRef.current
        );

        if (result.success) {
          setVideoStatus("ready");
          toast.success("Upload do vídeo concluído com sucesso!");
        } else {
          // O VideoUploadService já adiciona à fila para retry
          setVideoStatus("error");
          toast.warn("O vídeo será enviado automaticamente quando possível.");
        }

        return result;
      } catch (error) {
        setVideoStatus("error");
        setError({
          type: "upload_error",
          message: "Erro ao fazer upload do vídeo",
        });

        TelemetryService.error(
          "useEnhancedCamera",
          "video_upload_error" as EventType,
          {
            error: error instanceof Error ? error.message : "Erro desconhecido",
          },
          correlationIdRef.current
        );

        toast.error(
          "Ocorreu um erro durante o upload do vídeo. Tentaremos novamente mais tarde."
        );
        return {
          success: false,
          error: error instanceof Error ? error.message : "Erro desconhecido",
        };
      }
    },
    [originalBlob, videoMimeType, recordingDuration, setError]
  );

  return {
    // Refs do hook base
    previewRef,
    videoRef,
    chunksRef,
    canvasRef,

    // Estados do hook base
    mediaRecorder,
    videoUrl,
    videoSize,
    videoMimeType,
    originalBlob,
    isCaptured,
    hasPermissions,

    // Estados específicos
    videoStatus,
    recording: videoStatus === "recording",
    uploading: videoStatus === "uploading",
    error,
    recordingDuration,

    // Informações de conectividade
    isOnline,
    connectionQuality,

    // Métodos
    setIsCaptured,
    handleCapture,
    handleStart,
    handleStop,
    setVideoUrl,
    createVideoBase64,
    resetCamera,
    requestPermissions,
    uploadVideo,

    // ID para telemetria
    correlationId: correlationIdRef.current,

    // Novos estados
    hasCamera,
    hasMicrophone,
    checkDevicePermissions,
    initializeDevices,
  };
};

export default useEnhancedCamera;
